import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { VideoCardRow } from "@/components/video/VideoCardRow";
import type { VideoCardDisplay } from "@/components/video/types";
import {
  listPracticeLibraryItems,
  PracticeTopic,
  type PracticeLibraryEntry,
} from "@/lib/practice-hub.functions";
import { resolveBlogShareImageUrl } from "@/lib/blog-helpers";
import { m } from "@/paraglide/messages.js";

function practiceTopicLabel(topic: string): string {
  switch (topic) {
    case PracticeTopic.Putting:
      return m.practice_topic_putting();
    case PracticeTopic.Driving:
      return m.practice_topic_driving();
    case PracticeTopic.Chipping:
      return m.practice_topic_chipping();
    case PracticeTopic.Iron:
      return m.practice_topic_iron();
    case PracticeTopic.Wedge:
      return m.practice_topic_wedge();
    case PracticeTopic.FullSwing:
      return m.practice_topic_full_swing();
    default:
      return m.practice_badge_fallback();
  }
}

function thumbnailHueFromId(id: string): number {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) % 360;
  return hash;
}

function previewUrlFor(item: PracticeLibraryEntry): string | undefined {
  const resolved = resolveBlogShareImageUrl({
    shareImageUrl: item.preview_image_url,
    bodyMarkdown: item.body_markdown,
    defaultImageUrl: "",
  });
  return resolved || undefined;
}

function toVideoCardDisplay(item: PracticeLibraryEntry): VideoCardDisplay {
  return {
    id: item.id,
    title: item.title,
    excerpt: item.excerpt ?? "",
    thumbnailHue: thumbnailHueFromId(item.id),
    thumbnailUrl: previewUrlFor(item),
    badge: practiceTopicLabel(item.topic),
    to: "/practice/$slug",
    params: { slug: item.slug },
  };
}

export function PracticeLibraryTab() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["practice-library"],
    queryFn: () => listPracticeLibraryItems(),
  });

  if (isLoading) {
    return <p className="text-sm text-foreground/60">{m.practice_loading()}</p>;
  }

  if (isError) {
    return (
      <p className="text-sm text-destructive">
        {error instanceof Error ? error.message : m.practice_load_error()}
      </p>
    );
  }

  const posts = data ?? [];

  if (posts.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center">
        <p className="font-display text-lg font-semibold">{m.practice_empty_title()}</p>
        <p className="mt-2 text-sm text-muted-foreground">
          <Link to="/contact" className="text-accent underline-offset-4 hover:underline">
            {m.practice_empty_contact()}
          </Link>{" "}
          {m.practice_empty_more()}
        </p>
      </div>
    );
  }

  const topics = Object.values(PracticeTopic);

  return (
    <div className="space-y-6">
      {topics.map((topic) => {
        const items = posts.filter((x) => x.topic === topic).map(toVideoCardDisplay);
        if (items.length === 0) return null;
        return <VideoCardRow key={topic} title={practiceTopicLabel(topic)} items={items} />;
      })}
    </div>
  );
}
